/**
 * Clippr.ts - Clippr - Universal clipboard manager
 * 
 * CHANGE HISTORY:
 * - 251102_1138 [pending] danielbeer: Added FileMaker XML clipboard support and clipboard inspector
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */

import { FileMakerClipboard, getFileMakerClipboard } from './FileMakerClipboard';

export class Clippr {
    clipboard: any;
  history: any;
  maxHistory: any;
  listeners: any;
  slots: any;
  actr: any;

  constructor(options = {}) {
    this.actr = options.actr;
    this.clipboard = null;
    this.history = [];
    this.maxHistory = options.maxHistory || 10;
    this.listeners = new Set();
    this.slots = new Map(); // Named clipboard slots
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  
  /**
   * Get FileMaker clipboard instance
   */
  getFileMakerClipboard(): FileMakerClipboard {
    return getFileMakerClipboard();
  }
  
  /**
   * Write FileMaker XML to clipboard
   */
  async writeFileMakerXML(xml: string): Promise<boolean> {
    const fm = this.getFileMakerClipboard();
    return fm.writeFileMakerObject(xml);
  }
  
  /**
   * Read FileMaker XML from clipboard
   */
  async readFileMakerXML() {
    const fm = this.getFileMakerClipboard();
    return fm.readFileMakerObject();
  }
  
  /**
   * Inspect clipboard contents
   */
  async inspectClipboard() {
    const fm = this.getFileMakerClipboard();
    return fm.inspectClipboard();
  }
  
  /**
   * Get clipboard type counts
   */
  async getClipboardTypeCounts() {
    const fm = this.getFileMakerClipboard();
    return fm.getClipboardTypeCounts();
  }
  
  /**
   * Register Clippr actions with Actr
   * 
   * ARCHITECTURE NOTE: Clippr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from notifyr/groupr:
   * 1. Ephemeral State: Clipboard data is temporary
   *    - Data exists only in memory (circular buffer)
   *    - No persistence needed - cleared on app restart
   *    - CRUD would add unnecessary overhead
   * 
   * 2. System Integration: Clipboard is OS-level
   *    - Integrates with system clipboard (navigator.clipboard)
   *    - OpCentral doesn't own the data long-term
   *    - OS clipboard is source of truth
   *    - No need for OpCentral CRUD
   * 
   * 3. High-Frequency Operations: Copy/paste called constantly
   *    - Every copy/paste action by user
   *    - Performance-critical (must be instant)
   *    - CRUD overhead would degrade UX
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For clipboard operations (copy, cut, paste, clear)
   * - No CRUD: Data is ephemeral, OS-managed, not persisted
   * - Memory-based: History stored in circular buffer
   * 
   * Compare to other patterns:
   * - permittr: Actions-Only (performance-critical, in-memory cache)
   * - loggr: Actions-Only (write-heavy, external integrations)
   * - clippr: Actions-Only (ephemeral, OS-integrated, high-frequency)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/clippr/README.md "Actr Integration" section
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // copy action (high-frequency)
    this.actr.register('clippr:copy', {
      handler: async (params) => {
        return this.copy(params.data, params.options);
      },
      description: 'Copy data to clipboard'
    });
    
    // cut action
    this.actr.register('clippr:cut', {
      handler: async (params) => {
        return this.cut(params.data, params.options);
      },
      description: 'Cut data to clipboard'
    });
    
    // paste action (high-frequency)
    this.actr.register('clippr:paste', {
      handler: async (params) => {
        return this.paste(params.options);
      },
      description: 'Paste from clipboard'
    });
    
    // clear action
    this.actr.register('clippr:clear', {
      handler: async (params) => {
        return this.clear(params.slot);
      },
      description: 'Clear clipboard slot'
    });
    
    // Note: No CRUD operations - clipboard data is ephemeral and OS-managed.
    // History is stored in memory (circular buffer), not database.
  }
  
  /**
   * Copy data to clipboard
   * @param {*} data - Data to copy
   * @param {Object} options - Options (format, slot)
   */
  copy(data, options = {}): any {
    const {
      format = 'auto',
      slot = 'default',
      metadata = {}
    } = options;
    
    const clipboardItem = {
      data,
      format,
      metadata,
      timestamp: Date.now(),
      operation: 'copy'
    };
    
    // Store in slot
    this.slots.set(slot, clipboardItem);
    
    // Update main clipboard
    if (slot === 'default') {
      this.clipboard = clipboardItem;
      
      // Add to history
      this.history.unshift(clipboardItem);
      if (this.history.length > this.maxHistory) {
        this.history.pop();
      }
    }
    
    // Try to copy to system clipboard if it's text
    if (typeof data === 'string' && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(data).catch(err => {
        console.warn('Failed to write to system clipboard:', err);
      });
    }
    
    this.notifyListeners('copy', clipboardItem);
    
    return clipboardItem;
  }
  
  /**
   * Cut data (copy + mark for deletion)
   * @param {*} data - Data to cut
   * @param {Object} options - Options
   */
  cut(data, options = {}): any {
    const clipboardItem = this.copy(data, options);
    clipboardItem.operation = 'cut';
    
    this.notifyListeners('cut', clipboardItem);
    
    return clipboardItem;
  }
  
  /**
   * Paste data from clipboard
   * @param {Object} options - Options (slot, consume)
   */
  paste(options = {}): any {
    const {
      slot = 'default',
      consume = false // Remove from clipboard after paste
    } = options;
    
    const clipboardItem = this.slots.get(slot);
    
    if (!clipboardItem) {
      return null;
    }
    
    this.notifyListeners('paste', clipboardItem);
    
    // If it was a cut operation and we're consuming, remove it
    if (consume && clipboardItem.operation === 'cut') {
      this.slots.delete(slot);
      if (slot === 'default') {
        this.clipboard = null;
      }
    }
    
    return clipboardItem.data;
  }
  
  /**
   * Get clipboard contents without pasting
   * @param {string} slot - Clipboard slot
   */
  peek(slot = 'default'): any {
    const clipboardItem = this.slots.get(slot);
    return clipboardItem ? clipboardItem.data : null;
  }
  
  /**
   * Check if clipboard has data
   * @param {string} slot - Clipboard slot
   */
  hasData(slot = 'default'): boolean {
    return this.slots.has(slot);
  }
  
  /**
   * Clear clipboard
   * @param {string} slot - Clipboard slot (or 'all' for all slots)
   */
  clear(slot = 'default'): any {
    if (slot === 'all') {
      this.slots.clear();
      this.clipboard = null;
    } else {
      this.slots.delete(slot);
      if (slot === 'default') {
        this.clipboard = null;
      }
    }
    
    this.notifyListeners('clear', { slot });
  }
  
  /**
   * Get clipboard history
   */
  getHistory(): string {
    return [...this.history];
  }
  
  /**
   * Paste from history
   * @param {number} index - History index (0 = most recent)
   */
  pasteFromHistory(index): any {
    const item = this.history[index];
    if (item) {
      this.notifyListeners('paste', item);
      return item.data;
    }
    return null;
  }
  
  /**
   * Get all clipboard slots
   */
  getSlots(): string {
    return Array.from(this.slots.keys());
  }
  
  /**
   * Subscribe to clipboard events
   */
  subscribe(listener): any {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Notify listeners
   */
  notifyListeners(event, data): any {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Clippr listener error:', error);
      }
    });
  }
  
  /**
   * Export clipboard data (for persistence)
   */
  export(): any {
    return {
      clipboard: this.clipboard,
      history: this.history,
      slots: Array.from(this.slots.entries())
    };
  }
  
  /**
   * Import clipboard data (from persistence)
   */
  import(data): any {
    if (data.clipboard) {
      this.clipboard = data.clipboard;
    }
    if (data.history) {
      this.history = data.history;
    }
    if (data.slots) {
      this.slots = new Map(data.slots);
    }
  }
}

// Singleton instance
let instance = null;

export const getClipr = () => {
  if (!instance) {
    instance = new Clippr();
  }
  return instance;
};

export default Clippr;
